/* Markdown paste — detection + conversion. PURE of TipTap, so it stays testable.
 *
 * WHY THIS EXISTS AT ALL (the root cause, worth keeping):
 * Typed markdown already works — `# `, `- `, `> `, ```` ``` ```` come from
 * StarterKit's INPUT RULES. But ProseMirror input rules fire on TYPING and never
 * on paste, by design. So pasted markdown had nothing to convert it and landed as
 * literal text. This module is that missing half.
 *
 * It also explains the confusing part of the symptom: pasting from a RENDERED
 * source (a web page, a rendered chat answer) carries `text/html`, which
 * ProseMirror already parses through the schema and which therefore always
 * worked. Pasting markdown SOURCE (a .md file, a code block, a terminal) carries
 * only `text/plain`. Same gesture, opposite result, depending on the clipboard.
 *
 * WHY `marked` AND NOT TIPTAP'S OWN: TipTap 3.29 ships markdown SPEC helpers
 * (`@tiptap/core` → `markdown.createBlockMarkdownSpec`, and the `parseMarkdown`
 * hooks on nodes like TaskItem) but no tokenizer and no parse entry point —
 * `@tiptap/core` has zero dependencies. The hooks are scaffolding for a markdown
 * extension that does not exist in this version. If TipTap later ships one,
 * THIS module is what it replaces; the extension that calls it is a thin shim.
 *
 * The split follows editorCapabilities.js / slashItems.js: logic here (no TipTap
 * import, unit-testable under CRA jest), TipTap wiring in extensions/.
 */

import { marked } from 'marked';

/* Block-level signals. Each is anchored to line start, because that is what
   makes markdown markdown — a `#` mid-sentence is a hash, not a heading. */
const BLOCK_SIGNALS = [
	/^ {0,3}#{1,6}[ \t]+\S/m, // # Heading
	/^ {0,3}(?:```|~~~)/m, // fenced code
	/^ {0,3}>[ \t]?\S/m, // > quote
	/^ {0,3}[-*+][ \t]+\[[ xX]\][ \t]+/m, // - [ ] task
	/^ {0,3}[-*+][ \t]+\S/m, // - bullet
	/* Ordered lists need care: "2026. What a year." is a sentence, not a list.
	   CommonMark solves the same problem the same way — a numbered list may only
	   interrupt a paragraph when it starts at 1 — so accept either a list that
	   starts at 1, or two consecutive numbered lines (an outline pasted from
	   the middle of a document). */
	/^ {0,3}1[.)][ \t]+\S/m, // 1. ordered
	/^ {0,3}\d{1,9}[.)][ \t]+\S.*\n {0,3}\d{1,9}[.)][ \t]+\S/m, // 4. … 5. …
	/^ {0,3}(?:-{3,}|\*{3,}|_{3,})[ \t]*$/m, // --- rule
	/^ {0,3}\|?[ \t]*:?-{2,}:?[ \t]*(?:\|[ \t]*:?-{2,}:?[ \t]*)+\|?[ \t]*$/m, // table delimiter row
];

/* Inline signals. Deliberately NARROWER than what marked will actually render:
   single-asterisk italic and single-underscore emphasis are omitted because
   ordinary prose is full of stray asterisks and file_names_like_this, and a
   false positive here silently rewrites text the user pasted verbatim.
   Detection is conservative; CONVERSION, once triggered, is full fidelity. */
const INLINE_SIGNALS = [
	/!?\[[^\]\n]*\]\([^\s)]+\)/, // [link](url) / ![img](url)
	/\*\*[^\s*][^*]*\*\*/, // **bold**
	/__[^\s_][^_]*__/, // __bold__
	/~~[^\s~][^~]*~~/, // ~~strike~~
	/`[^`\n]+`/, // `code`
];

/* Tags that mean the clipboard HTML is REAL rich text rather than a wrapper.
   Editors and terminals wrap plain text in <div>/<span style> when they put
   text/html on the clipboard — treating that as rich text would defeat the whole
   feature, since copying markdown out of VS Code is the main use case. So the
   test is for SEMANTIC structure, not for the mere presence of html. */
const SEMANTIC_HTML = /<\s*(h[1-6]|ul|ol|li|table|tr|td|th|blockquote|strong|b|em|i|code|a)\b/i;

/** Does this clipboard HTML carry real structure worth preferring over the text? */
export function htmlIsSemantic(html) {
	if (!html || typeof html !== 'string') return false;
	// Strip the comment fences Word/Office wrap their fragments in before testing.
	return SEMANTIC_HTML.test(html.replace(/<!--[\s\S]*?-->/g, ''));
}

/**
 * Is this plain text markdown worth converting?
 *
 * One block signal is enough — block syntax is unambiguous and line-anchored.
 * Inline syntax needs TWO, because any single one can occur by accident in prose.
 */
export function looksLikeMarkdown(text) {
	if (!text || typeof text !== 'string') return false;
	if (!text.trim()) return false;

	if (BLOCK_SIGNALS.some((re) => re.test(text))) return true;

	const inlineHits = INLINE_SIGNALS.filter((re) => re.test(text)).length;
	return inlineHits >= 2;
}

/* Defensive strip before parsing. marked passes raw HTML through by design, and
   while TipTap's schema drops unknown nodes on insert, script/style TEXT could
   still survive as visible characters. Cheap to remove, so remove it. */
function stripRawHtmlHazards(md) {
	return md
		.replace(/<\s*(script|style|iframe|object|embed)\b[\s\S]*?<\s*\/\s*\1\s*>/gi, '')
		.replace(/<\s*(script|style|iframe|object|embed)\b[^>]*\/?>/gi, '');
}

/* GFM task lists → TipTap's task-list shape.
 *
 * marked emits `<li><input type="checkbox" checked disabled> text</li>`, but
 * TaskItem parses `li[data-type="taskItem"]` and reads `data-checked`. Without
 * this pass a pasted checklist arrives as a bullet list with a stray, dead
 * checkbox glyph — which looks like the feature half-working, the worst outcome.
 *
 * Done over a parsed DOM rather than with regex: nested lists make the
 * `<li>`→`<ul>` relationship structural, and a regex cannot see structure.
 *
 * If the mount lacks the `tasks` capability the schema has no taskItem, and
 * these degrade to plain list items — correct, and by then the dead <input> is
 * already gone.
 */
function normalizeTaskLists(doc) {
	doc.querySelectorAll('li input[type="checkbox"]').forEach((input) => {
		const li = input.closest('li');
		if (!li) return;

		const checked = input.hasAttribute('checked') || input.checked === true;
		input.remove();

		// marked leaves the space that followed the checkbox; it would render as
		// a leading indent on every item.
		const first = li.firstChild;
		if (first && first.nodeType === 3) {
			first.textContent = first.textContent.replace(/^[ \t]+/, '');
		}

		li.setAttribute('data-type', 'taskItem');
		li.setAttribute('data-checked', checked ? 'true' : 'false');

		const list = li.parentElement;
		if (list && list.tagName === 'UL') list.setAttribute('data-type', 'taskList');
	});
}

/**
 * Markdown → HTML that this editor's schema understands.
 * Returns '' when there is nothing to insert, so a caller can fall through to
 * the default paste rather than clearing the selection with an empty insert.
 */
export function markdownToHtml(text) {
	if (!text || typeof text !== 'string') return '';

	let html;
	try {
		html = marked.parse(stripRawHtmlHazards(text), {
			gfm: true, // tables, task lists, strikethrough, autolinks
			breaks: false, // a single newline is not a hard break in real markdown
			async: false,
		});
	} catch {
		// A parser failure must never eat the paste — fall through to plain text.
		return '';
	}

	if (!html || !html.trim()) return '';

	if (typeof DOMParser === 'undefined') return html;
	const doc = new DOMParser().parseFromString(html, 'text/html');
	normalizeTaskLists(doc);
	return doc.body.innerHTML;
}
